/** What the sign-in pages share: where to go after, the steps between them, and submitting their forms. */
import { useState } from "preact/hooks";
import { type ApiError, authPost, providerLogin, queryString } from "../../api/client.ts";
import type { AppLoginOut, AuthStep, Finished } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { post, shell } from "../../lib/native.ts";
import { asApiError } from "../../lib/resource.ts";
import { location, navigate } from "../../lib/router.ts";
import { signedIn } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";

/** The sign-in pages, by path. */
export type AuthPath = "/signin" | "/signup" | "/code" | "/forgot-password" | "/reset-password";

/** The shortest password taken (the server's, and its identity provider's, policy). */
export const MIN_PASSWORD = 10;

/** Only the app's own paths: no `//elsewhere`, no schemes. */
export function safeReturn(r: string | null | undefined): string {
  return r?.startsWith("/") && !r.startsWith("//") && !r.includes("\\") ? r : "/";
}

/** Where to go once signed in: the `return_to` of this page's address. */
export const returnTo = (): string => safeReturn(location.value.searchParams.get("return_to"));

/** A sign-in page's address, keeping where to go after (and `extra` parameters). */
export function authPath(path: AuthPath, extra: Record<string, string> = {}, to = returnTo()): string {
  return `${path}${queryString({ ...(to === "/" ? {} : { return_to: to }), ...extra })}`;
}

/** Goes on from a sign-in step: signed in, where they were going; or to the page asking for the emailed code. */
export async function next(step: AuthStep): Promise<void> {
  if (step.step === "done") {
    const to = returnTo();
    await signedIn();
    navigate(to, true);
  } else {
    navigate(authPath("/code", { email: step.email ?? "" }));
  }
}

/**
 * Signs in on the identity provider's pages (single sign-on, steps these pages don't do), then on to `to`. In the
 * native app, in its sign-in sheet (the provider's pages aren't let into its web views, and the sheet's cookies aren't
 * theirs): the sheet ends with a code, which signs in here (`/finish`). Elsewhere (an app that has no sheet), the page
 * goes there itself. Throws ApiError; closed without signing in, nothing.
 */
async function providerSignIn(to: string, email = ""): Promise<void> {
  if (!shell()) {
    providerLogin(to, email);
    return;
  }
  const start = await authPost<AppLoginOut>("/app-login", { return_to: to, login_hint: email });
  const ended = await post("signIn", { url: start.url });
  if (ended === "unsupported") {
    providerLogin(to, email);
    return;
  }
  const back = typeof ended === "string" ? new URL(ended).searchParams : null;
  const code = back?.get("code");
  if (!(back && code)) {
    return;
  }
  const done = await authPost<Finished>("/finish", { code, returned: back.get("state") ?? "", state: start.state });
  await signedIn();
  navigate(done.return_to, true);
}

/** `providerSignIn`, what goes wrong said (a toast). */
export function signInElsewhere(to: string, email = ""): void {
  providerSignIn(to, email).catch((e: unknown) => toast(asApiError(e).message, "error"));
}

/** The field an error is about, if a field shows it: an email that isn't one, a password not taken. */
export function fieldOf(error: ApiError): "email" | "password" | null {
  if (error.code === "weak_password") {
    return "password";
  }
  return error.code === "invalid" && error.info["path"] === "email" ? "email" : null;
}

/** The error for the `field`'s note, if it's about it. */
export function fieldError(error: ApiError | null, field: "email" | "password"): string | null {
  if (!error || fieldOf(error) !== field) {
    return null;
  }
  return field === "email" ? t("auth.badEmail") : error.message;
}

/** A form's submission: whether it's running, and what went wrong (shown with the form, until the next try). */
export function useSubmit(fn: () => Promise<void>): { submit: () => void; busy: boolean; error: ApiError | null } {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const submit = () => {
    setBusy(true);
    setError(null);
    fn().then(
      () => setBusy(false),
      (e: unknown) => {
        setBusy(false);
        setError(asApiError(e));
      },
    );
  };
  return { submit, busy, error };
}
